import React, { useState, useCallback } from 'react';
import { Box, Typography, TextareaAutosize, Button, Alert, useTheme } from '@mui/material';
import DemoDashboardHome from './DemoDashboardHome';
import MetricCardsWidget, { MetricCardsWidgetProps } from 'components/Widgets/MetricCardsWidget';
import CollapsibleMenuWidget from 'components/Widgets/CollapsibleMenuWidget';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { useNavigate } from 'react-router-dom';



const defaultMenuConfig = {
  module: 'D&AI Monthly XOps',
  subCategories: [
    { label: 'Summary', icon: 'summary-icon', route: '/xops/summary' },
    { label: 'Reporting', icon: 'report-icon', route: '/xops/reporting' },
    { label: 'Department Performance', icon: 'department-icon', route: '/xops/department' },
    {
      label: 'Platform Operations', icon: 'ops-icon', route: '/xops/platform',
      subCategories: [
        { label: 'Overview', route: '/xops/platform/overview' },
        { label: 'Monitoring', route: '/xops/platform/monitoring' },
        {
          label: 'Incident Management', route: '/xops/platform/incidents',
          subCategories: [
            { label: 'Open Incidents', route: '/xops/platform/incidents/open' },
            { label: 'Closed Incidents', route: '/xops/platform/incidents/closed' },
          ],
        },
      ],
    },
    { label: 'Experience & Feedback', icon: 'experience-icon', route: '/xops/experience' },
    { label: 'Governance & Planning', icon: 'governance-icon', route: '/xops/governance' },
  ],
  defaultOpen: true,
};

interface DemoDashboardWithWidgetsProps {
  widget: 'metric-cards' | 'collapsible-menu';
}

const DemoDashboardWithWidgets: React.FC<DemoDashboardWithWidgetsProps> = ({ widget }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const defaultMetricConfig: MetricCardsWidgetProps = {
    title: 'Key Performance Indicators',
    metrics: [
      {
        label: 'Happiness Index',
        value: 90,
        trend: '+5%',
        color: 'green',
        tooltipExplanation: '+5% vs last month'
      },
  
      {
        label: 'MTTR',
        value: '5',
        unit: 'Days',
        trend: '-5%',
        color: 'red',
        tooltipExplanation: ''
      },
  
      {
        label: 'Availability',
        value: '94',
        unit: '%',
        trend: '-2%',
        color: 'red',
        tooltipExplanation: '-2% vs last month'
      },
  
      {
        label: 'SLA Compliance',
        value: '92.5',
        unit: '%',
        trend: '+7.5%',
        color: 'green',
        tooltipExplanation: '+7.5% vs last month'
      },
  
      {
        label: 'Total Incidents',
        value: '250',
        trend: '+20%',
        color: 'green',
        tooltipExplanation: '+20% vs last month'
      }
    ],
  
    orientation: 'horizontal',
  
  };
  const [metricConfig, setMetricConfig] = useState<MetricCardsWidgetProps>(defaultMetricConfig);
  const [metricConfigText, setMetricConfigText] = useState(JSON.stringify(defaultMetricConfig, null, 2));
  const [metricError, setMetricError] = useState('');

  const [menuConfig, setMenuConfig] = useState(defaultMenuConfig);
  const [menuConfigText, setMenuConfigText] = useState(JSON.stringify(defaultMenuConfig, null, 2));
  const [menuError, setMenuError] = useState('');

  const applyMetricConfig = useCallback(() => {
    try {
      const parsedConfig = JSON.parse(metricConfigText) as MetricCardsWidgetProps;
      setMetricConfig(parsedConfig);
      setMetricError('');
    } catch (e: any) {
      setMetricError('Invalid JSON for Metric Cards: ' + e.message);
    }
  }, [metricConfigText]);

  const applyMenuConfig = useCallback(() => {
    try {
      const parsedConfig = JSON.parse(menuConfigText);
      setMenuConfig(parsedConfig);
      setMenuError('');
    } catch (e: any) {
      setMenuError('Invalid JSON for Collapsible Menu: ' + e.message);
    }
  }, [menuConfigText]);

  return (
    <PageWrapper>
      <PageHeader title={widget === 'metric-cards' ? 'Metrics Summary Widget' : 'Collapsible menu'}>
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>
      <Box sx={{ padding: 2 }}>

        {widget === 'metric-cards' && (
          <Box sx={{ display: 'flex', gap: 4, marginBottom: 4, flexDirection: 'column' }}>
            <Box sx={{ flex: 1, display: 'flex', gap: 4, flexDirection: 'column' }}>
              <Typography color={theme.palette.textLight.dark} variant="h6">Metric Cards Widget</Typography>
              <MetricCardsWidget {...metricConfig} />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', gap: 4, flexDirection: 'column' }}>
              <Typography color={theme.palette.textLight.dark} variant="h6">Metric Cards Configuration (JSON)</Typography>
              <TextareaAutosize
                minRows={10}
                maxRows={20}
                value={metricConfigText}
                onChange={(e) => setMetricConfigText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
              <Button variant="contained" onClick={applyMetricConfig} sx={{ mt: 1 }}>
                Apply Metric Config
              </Button>
              {metricError && <Alert severity="error" sx={{ mt: 2 }}>{metricError}</Alert>}
            </Box>
          </Box>
        )}

        {widget === 'collapsible-menu' && (
          <Box sx={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            <Box sx={{ flex: 1, display: 'flex', gap: 4, flexDirection: 'column' }}>
              <Typography color={theme.palette.textLight.dark} variant="h6">Collapsible Menu Widget</Typography>
              <CollapsibleMenuWidget {...menuConfig} />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', gap: 4, flexDirection: 'column' }}>
              <Typography color={theme.palette.textLight.dark} variant="h6">Collapsible Menu Configuration (JSON)</Typography>
              <TextareaAutosize
                minRows={10}
                maxRows={20}
                value={menuConfigText}
                onChange={(e) => setMenuConfigText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
              <Button variant="contained" onClick={applyMenuConfig} sx={{ mt: 1 }}>
                Apply Menu Config
              </Button>
              {menuError && <Alert severity="error" sx={{ mt: 2 }}>{menuError}</Alert>}
            </Box>
          </Box>
        )}

        {/* <Typography variant="h5" gutterBottom>Original Demo Dashboard Home</Typography> */}
        {/* <DemoDashboardHome /> */}
      </Box>
    </PageWrapper>
  );
};

export default DemoDashboardWithWidgets;